/**
 * Glow Up Tracker — admin-only checklist for the Memorial Day content series.
 *
 * 28 shots · 17 days · personal tracking. State persists in localStorage so
 * Derwin doesn't need a backend round-trip for a private tool. Each shot has
 * a checkbox + optional photo URL field + optional note.
 *
 * Route: /admin/glow-up (AdminRoute-gated)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, Camera, Download, RotateCcw } from 'lucide-react';

const STORAGE_KEY = 'turfpulse-glow-up-tracker-v1';

const PHASES = [
  { key: 'phase-1', label: 'Phase 1 · Foundation Week', dates: '5/9–5/15', tone: 'emerald' },
  { key: 'phase-2', label: 'Phase 2 · WH52 Drop Week', dates: '5/16–5/22', tone: 'amber' },
  { key: 'phase-3', label: 'Phase 3 · Memorial Day Reveal', dates: '5/23–5/25', tone: 'rose' },
];

const SHOTS = [
  // Phase 1
  { id: 1,  phase: 'phase-1', day: 'Sat 5/9 AM',      title: 'Wide front yard hero (the "before")',                  type: 'Photo · landscape',  caption: 'Day 1 of the Glow Up. Honest baseline.',                    pro: 'Stand at the curb, golden hour 7-8 AM Sat (your only AM window). Mark the spot — same angle every weekend.' },
  { id: 2,  phase: 'phase-1', day: 'Sat 5/9',          title: 'Close-up of one bumpy/thin patch',                     type: 'Photo · macro',       caption: 'This is the spot I\'m fixing. Tag it.',                       pro: 'Get on your knees. Phone 6" off the grass. Show texture honestly.' },
  { id: 3,  phase: 'phase-1', day: 'Sat 5/9',          title: 'Soil probe in the ground (CH1 DRY)',                   type: 'Photo · top-down',    caption: 'Probe 1 of 3. The dry spot. The trigger.',                  pro: 'Make sure cable is visible — story is "this is wired in".' },
  { id: 4,  phase: 'phase-1', day: 'Sun 5/10 PM',     title: 'Saturday→Sunday before/after of one zone',             type: 'Side-by-side',        caption: '24 hours after a smart fire. Watch the moisture climb.',    pro: 'Shoot from same angle. Soil moisture chart screenshot underneath.' },
  { id: 5,  phase: 'phase-1', day: 'Mon 5/11 evening', title: 'TurfPulse /widget wall display',                       type: 'Photo · screen',      caption: 'My wall HQ. No app to open. Just glance.',                   pro: 'Post a Reel pan across all the tiles.' },
  { id: 6,  phase: 'phase-1', day: 'Mon 5/11',         title: 'The Ecowitt depletion curve screenshot',               type: 'Photo · UI',          caption: '5 days of dry-down. Roots training themselves.',             pro: 'Use today\'s 5/3-5/8 chart — it\'s gorgeous.' },
  { id: 7,  phase: 'phase-1', day: 'Wed 5/13 evening', title: 'Mowing time-lapse',                                     type: 'Video · 30 sec',      caption: 'Evening mow club. After 5:30 only. Bermuda doesn\'t care.',  pro: 'Tripod on the porch. "Post-work warrior" angle.' },
  { id: 8,  phase: 'phase-1', day: 'Wed 5/13',         title: 'Mower stripes from the deck',                          type: 'Photo · golden hour', caption: 'Stripes after 12 hrs of work. Therapy hits different.',     pro: 'Shoot ~7:30 PM, sun low behind, stripes lean toward camera.' },
  { id: 9,  phase: 'phase-1', day: 'Thu 5/14',         title: 'Soil probe data → Rachio fire moment',                 type: 'Video · 15 sec',      caption: 'When the dirt says GO, the controller listens.',            pro: 'Screen-record the activity feed showing a fire event.' },
  { id: 10, phase: 'phase-1', day: 'Fri 5/15',         title: '"Week 1 Receipts" carousel',                            type: 'Photo · 5 slides',    caption: 'Slide 1: starting moisture · Slide 2: minutes watered · Slide 3: dollars saved · Slide 4: depletion slope · Slide 5: this Saturday\'s plan',  pro: 'Native FB carousel = highest engagement format.' },

  // Phase 2
  { id: 11, phase: 'phase-2', day: 'Sat 5/16 AM',      title: 'Week 2 hero (same angle as #1)',                       type: 'Photo · landscape',   caption: 'Week 2. Stripes hold. Here\'s how.',                          pro: 'Compare side-by-side to #1 in the post.' },
  { id: 12, phase: 'phase-2', day: 'Sat 5/16',         title: 'The planter "before" — patchy/thin',                   type: 'Photo · close-up',    caption: 'Zone 1 / Planter — the next problem to solve.',             pro: 'Sets up the per-zone reveal next week.' },
  { id: 13, phase: 'phase-2', day: 'Mon 5/19 — WH52 ARRIVAL', title: 'Unboxing',                                       type: 'Video · 60 sec',      caption: 'The probe that changed the game. WH52 — moisture, soil temp, AND salinity.',   pro: 'Phone propped, slow unbox, show the 3-prong sensor.' },
  { id: 14, phase: 'phase-2', day: 'Mon 5/19',         title: 'Probe in hand, packaging scattered',                   type: 'Photo · flatlay',     caption: 'What $40 of soil intelligence looks like.',                   pro: 'Top-down on a wood porch, package + probe + a beer 🍺.' },
  { id: 15, phase: 'phase-2', day: 'Tue 5/20 evening', title: 'Burying the WH52 in the planter',                      type: 'Video · 30 sec',      caption: 'Setting up Zone 1 to fire on its OWN data, not the lawn average.', pro: 'Shoot dig + insert + cover in one take. Add lyrics overlay later.' },
  { id: 16, phase: 'phase-2', day: 'Tue 5/20',         title: 'First WH52 reading on the dashboard',                  type: 'Photo · screen',      caption: 'First read. EC 850 μS/cm. Ideal. Already learning my soil.',  pro: 'Screenshot the new EC row + zoom in.' },
  { id: 17, phase: 'phase-2', day: 'Wed 5/21',         title: 'The TurfPulse Sensor Placement page',                   type: 'Photo · screen',      caption: 'Mapping CH4 → Zone 1 / Planter. The system gets smarter every probe I add.', pro: 'Highlight the dropdown selecting "Planter · 30% MAD".' },
  { id: 18, phase: 'phase-2', day: 'Thu 5/22 evening', title: 'Side-by-side: lawn AVG probe vs Planter probe',        type: 'Photo · split',       caption: 'Two probes. Two stories. One brain.',                         pro: 'Two phone screenshots stitched in CapCut.' },
  { id: 19, phase: 'phase-2', day: 'Fri 5/22',         title: '"Week 2 Receipts" carousel',                            type: 'Photo · 5 slides',    caption: 'Same format as #10 but with WH52 data.',                       pro: 'End every work week with a receipt post — patterned reliability.' },

  // Phase 3
  { id: 20, phase: 'phase-3', day: 'Sat 5/23 AM',      title: 'The hero shot (drone if possible)',                    type: 'Photo or video',      caption: '17 days. Here\'s the receipt.',                                 pro: 'Sat 7-8 AM golden hour. Ladder for elevation if no drone.' },
  { id: 21, phase: 'phase-3', day: 'Sat 5/23',         title: 'Bare feet on the lawn',                                type: 'Photo · macro',       caption: 'Smooth like that. 🎷',                                          pro: 'Quiet plant of the tribute song lyric.' },
  { id: 22, phase: 'phase-3', day: 'Sat 5/23',         title: 'Three weeks of data in one screen',                    type: 'Photo · UI',          caption: 'The receipts. Every fire, every skip, every drop saved.',     pro: 'Activity feed scrolled to Day 1.' },
  { id: 23, phase: 'phase-3', day: 'Sun 5/24 7PM CST', title: 'The 30-second anthem Reel drop',                       type: 'Video · 30 sec',      caption: 'I built a smart lawn-care app. Then I wrote a funk song about it because that\'s apparently who I am now. 🎺',  pro: 'Sprinkler popping on time-lapse synced to the chorus drop. POST AT 7 PM CST.' },
  { id: 24, phase: 'phase-3', day: 'Sun 5/24 7:30PM',  title: 'The Georgetown Lawn Leveling tribute drop',            type: 'Video · 60 sec',      caption: 'There\'s another one — for the folks who taught me. @Georgetown Lawn Leveling 🎷',  pro: 'Stand-alone post 30 min after the anthem. Two songs in one day = legendary.' },
  { id: 25, phase: 'phase-3', day: 'Mon 5/25 — Memorial Day', title: 'Full timeline carousel',                          type: 'Photo · 10 slides',   caption: 'The complete arc: Day 1 → Day 17 + the chart + the song + the link.', pro: 'Pin this as your top FB post for the week.' },
  { id: 26, phase: 'phase-3', day: 'Mon 5/25',         title: 'Sit-on-the-porch shot',                                type: 'Photo · environmental', caption: 'Holiday Monday. Lawn handles itself. Anthem on the speaker.', pro: 'Wife/kid/dog/beer in frame = humanizes the founder story.' },
  { id: 27, phase: 'phase-3', day: 'Mon 5/25',         title: 'Inner Circle crest screenshot',                        type: 'Photo · UI',          caption: '10 founders. First 10 ever. Doors close at 10.',              pro: 'Drives last-call urgency for FB DMs.' },
  { id: 28, phase: 'phase-3', day: 'Mon 5/25 PM',      title: 'The Outro Reel — looking at the wall HQ',              type: 'Video · 15 sec',      caption: 'Built it. Lived it. Wrote a song about it. 🛡️ Thanks for ridin\' along.', pro: 'Tripod inside, walking past the kiosk in slow-mo.' },
];

const TONE_CLASSES = {
  emerald: 'border-emerald-300 dark:border-emerald-800 bg-emerald-50/40 dark:bg-emerald-950/20',
  amber:   'border-amber-300 dark:border-amber-800 bg-amber-50/40 dark:bg-amber-950/20',
  rose:    'border-rose-300 dark:border-rose-800 bg-rose-50/40 dark:bg-rose-950/20',
};

const AdminGlowUpPage = () => {
  // Lazy initializer reads localStorage synchronously on first render so
  // the persist-effect doesn't immediately overwrite saved state with {}.
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Persist on change. First mount writes the same data back (harmless).
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch { /* quota exceeded, ignore */ }
  }, [state]);

  const update = (id, field, value) => {
    setState((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const stats = useMemo(() => {
    const done = SHOTS.filter((s) => state[s.id]?.done).length;
    return { done, total: SHOTS.length, pct: Math.round((done / SHOTS.length) * 100) };
  }, [state]);

  const exportJSON = () => {
    const out = SHOTS.map((s) => ({ ...s, ...(state[s.id] || {}) }));
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `glow-up-tracker-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetAll = () => {
    if (!window.confirm('Reset ALL shot tracking? This clears your local checklist.')) return;
    setState({});
  };

  return (
    <>
      <Helmet>
        <title>Glow Up Tracker — TurfPulse Admin</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main className="container mx-auto max-w-5xl px-4 py-8" data-testid="glow-up-page">
        <div className="mb-6 flex items-center justify-between gap-2 flex-wrap">
          <Button variant="ghost" size="sm" asChild data-testid="glow-up-back-button">
            <Link to="/dashboard"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
          </Button>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={exportJSON} data-testid="glow-up-export">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export JSON
            </Button>
            <Button size="sm" variant="ghost" onClick={resetAll} data-testid="glow-up-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
            </Button>
          </div>
        </div>

        <div className="mb-8" data-testid="glow-up-hero">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight">Memorial Day Glow Up</h1>
          <p className="text-muted-foreground mt-1">
            28 shots · 17 days · 5/9 → 5/25 · Personal content tracker (saves to your browser, not the cloud)
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-500 via-amber-500 to-rose-500 transition-all"
                style={{ width: `${stats.pct}%` }}
                data-testid="glow-up-progress-bar"
              />
            </div>
            <div className="text-sm font-mono whitespace-nowrap" data-testid="glow-up-progress">
              {stats.done}/{stats.total} · {stats.pct}%
            </div>
          </div>
        </div>

        {PHASES.map((phase) => {
          const phaseShots = SHOTS.filter((s) => s.phase === phase.key);
          const phaseDone = phaseShots.filter((s) => state[s.id]?.done).length;
          return (
            <div key={phase.key} className="mb-8" data-testid={`glow-up-phase-${phase.key}`}>
              <h2 className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">
                {phase.label} · <span className="font-mono">{phase.dates}</span> · {phaseDone}/{phaseShots.length} done
              </h2>
              <div className="space-y-2">
                {phaseShots.map((shot) => {
                  const s = state[shot.id] || {};
                  return (
                    <Card
                      key={shot.id}
                      className={`${TONE_CLASSES[phase.tone]} ${s.done ? 'opacity-60' : ''}`}
                      data-testid={`glow-up-shot-${shot.id}`}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={!!s.done}
                            onCheckedChange={(v) => update(shot.id, 'done', !!v)}
                            className="mt-1"
                            data-testid={`glow-up-check-${shot.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <div className="font-semibold text-sm">
                                <span className="font-mono text-muted-foreground mr-2">#{shot.id}</span>
                                {shot.title}
                              </div>
                              <div className="flex gap-1 flex-wrap">
                                <Badge variant="outline" className="text-[9px] uppercase">{shot.day}</Badge>
                                <Badge variant="outline" className="text-[9px] uppercase">{shot.type}</Badge>
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1 italic">📤 {shot.caption}</p>
                            <p className="text-xs text-muted-foreground mt-1">💡 {shot.pro}</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                              <Input
                                value={s.photoUrl || ''}
                                onChange={(e) => update(shot.id, 'photoUrl', e.target.value)}
                                placeholder="Photo URL (optional)"
                                className="text-xs"
                                data-testid={`glow-up-photo-${shot.id}`}
                              />
                              <Input
                                value={s.note || ''}
                                onChange={(e) => update(shot.id, 'note', e.target.value)}
                                placeholder="Note (optional)"
                                className="text-xs"
                                data-testid={`glow-up-note-${shot.id}`}
                              />
                            </div>
                            {s.photoUrl && (
                              <div className="mt-2 flex items-center gap-2">
                                <Camera className="w-3 h-3 text-muted-foreground" />
                                <a
                                  href={s.photoUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-emerald-600 underline truncate"
                                  data-testid={`glow-up-photo-link-${shot.id}`}
                                >
                                  view photo
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div className="mt-12 text-center text-xs text-muted-foreground">
          State stored locally on this device. Use Export JSON before clearing browser data.
        </div>
      </main>
    </>
  );
};

export default AdminGlowUpPage;
